import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import StockChartModal from '../components/StockChartModal';
import { 
  getGroupedScanHistory, 
  triggerPerformanceTrackingByDate,
  trackPerformanceSingle,
  deleteScanResult,
  deleteScansByDate
} from '../api/historyApi';

const ResultsHistoryPage = () => {
  const [searchParams] = useSearchParams();
  /** TrendVector mobile WebView (`tv_embed=1`): hide site chrome; back button is in the app. */
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  // Original unfiltered data and filtered data for display
  const [originalResults, setOriginalResults] = useState({});
  const [filteredResults, setFilteredResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    model_type: '',
    start_date: '',
    end_date: '',
    search_query: ''
  });
  
  // Accordion state
  const [openDates, setOpenDates] = useState({});
  const [openModels, setOpenModels] = useState({});
  
  // Performance tracking state
  const [trackingInProgress, setTrackingInProgress] = useState(false);
  
  // Delete modal states
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // 'date' or 'result'
    targetId: null,
    targetDate: null,
    targetSymbol: null
  });
  const [deleting, setDeleting] = useState(false);
  
  // Chart modal states
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedSymbolForChart, setSelectedSymbolForChart] = useState(null);
  
  // Expanded rows removed in grouped layout

  /**
   * Apply client-side filters to the original results
   */
  const applyFilters = useCallback(() => {
    if (!originalResults || Object.keys(originalResults).length === 0) {
      setFilteredResults({});
      return;
    }

    let filtered = {};

    // Iterate over each date in the original results
    Object.keys(originalResults).forEach((dateKey) => {
      // Filter by date range
      if (filters.start_date && dateKey < filters.start_date) return;
      if (filters.end_date && dateKey > filters.end_date) return;

      const modelsForDate = originalResults[dateKey];
      const filteredModels = {};

      // Iterate over each model type
      Object.keys(modelsForDate).forEach((modelKey) => {
        // Filter by model type
        if (filters.model_type && modelKey !== filters.model_type) return;

        const results = modelsForDate[modelKey];
        let filteredStocks = results;

        // Filter by search query
        if (filters.search_query) {
          const query = filters.search_query.toLowerCase();
          filteredStocks = results.filter((result) =>
            result.stock_symbol?.toLowerCase().includes(query)
          );
        }

        // Only include this model if it has results after filtering
        if (filteredStocks.length > 0) {
          filteredModels[modelKey] = filteredStocks;
        }
      });

      // Only include this date if it has models with results
      if (Object.keys(filteredModels).length > 0) {
        filtered[dateKey] = filteredModels;
      }
    });

    setFilteredResults(filtered);
  }, [originalResults, filters]);

  /**
   * Fetch scan history and store as both original and filtered
   */
  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getGroupedScanHistory();
      if (response.success) {
        const data = response.data || {};
        setOriginalResults(data);
        setFilteredResults(data); // Initially, filtered = original
      } else {
        setError('Failed to fetch grouped scan history');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(err.response?.data?.detail || 'Failed to fetch scan history');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch results on component mount and when dependencies change
   */
  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  /**
   * Reset all filters and show original results
   */
  const handleResetFilters = () => {
    setFilters({
      model_type: '',
      start_date: '',
      end_date: '',
      search_query: ''
    });
    // Reset to original unfiltered data
    setFilteredResults(originalResults);
  };

  /**
   * Open delete confirmation modal
   */
  const openDeleteModal = (type, data) => {
    setDeleteModal({
      isOpen: true,
      type,
      targetId: data.id,
      targetDate: data.date,
      targetSymbol: data.symbol
    });
  };

  /**
   * Close delete confirmation modal
   */
  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      type: null,
      targetId: null,
      targetDate: null,
      targetSymbol: null
    });
  };

  /**
   * Handle deletion confirmation
   */
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setError(null);
    
    try {
      if (deleteModal.type === 'date') {
        // Delete all scans for a date
        await deleteScansByDate(deleteModal.targetDate);
        setSuccessMessage(`All scan results for ${deleteModal.targetDate} have been deleted successfully.`);
      } else if (deleteModal.type === 'result') {
        // Delete a single result
        await deleteScanResult(deleteModal.targetId);
        setSuccessMessage(`Scan result for ${deleteModal.targetSymbol} has been deleted successfully.`);
      }
      
      // Close modal and refresh data
      closeDeleteModal();
      await fetchResults();
    } catch (err) {
      console.error('Error deleting:', err);
      setError(err.response?.data?.detail || 'Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Trigger performance tracking for a specific date
   * This initiates a background job that runs asynchronously
   */
  const handleTrackPerformanceByDate = async (dateKey) => {
    setTrackingInProgress(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      const response = await triggerPerformanceTrackingByDate(dateKey);
      
      if (response.success) {
        // Show user-friendly message that the job has started
        setSuccessMessage(response.message || `Success! Performance tracking for ${dateKey} has started in the background. Please check back in a few minutes and refresh the page to see the updated results.`);
        
        // Auto-refresh results after a delay
        setTimeout(() => {
          fetchResults();
        }, 2000);
      }
    } catch (err) {
      console.error('Error tracking performance:', err);
      setError(err.response?.data?.detail || 'Failed to start performance tracking');
    } finally {
      // Set loading state back to false immediately since the job is running in background
      setTrackingInProgress(false);
    }
  };

  // Per-row performance loader removed in grouped view

  // Row expansion logic removed

  const toggleDate = (dateKey) => {
    setOpenDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  const toggleModel = (dateKey, modelKey) => {
    const key = `${dateKey}__${modelKey}`;
    setOpenModels(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTrackSingle = async (resultId) => {
    try {
      await trackPerformanceSingle(resultId);
      await fetchResults();
    } catch (err) {
      console.error('Error tracking single result:', err);
      setError(err.response?.data?.detail || 'Failed to update this stock');
    }
  };

  // Date formatting not required in grouped view

  // Badge colors not used in grouped view

  /**
   * Format model type name
   */
  const formatModelType = (modelType) => {
    const names = {
      'short-term': 'Short-Term',
      'long-term': 'Long-Term',
      'swing-model': 'Swing Model',
      'fno-breakout': 'F&O Breakout',
      'fno-breakdown': 'F&O Breakdown'
    };
    return names[modelType] || modelType;
  };

  /**
   * Calculate current performance from API data
   * Now uses percent_change directly from the result object
   */
  const calculatePerformance = (result) => {
    console.log(`--- DEBUG: calculatePerformance called for ${result.stock_symbol} ---`);
    console.log(`--- DEBUG: result.performance?:`, result?.performance);
    const nestedPercent = result?.performance?.percent_change;
    console.log(`--- DEBUG: result.performance?.percent_change:`, nestedPercent);
    
    // First, check if the nested performance object has percent_change from the API
    if (nestedPercent !== null && nestedPercent !== undefined) {
      console.log(`--- DEBUG: Using nested performance.percent_change:`, nestedPercent);
      return nestedPercent;
    }
    // No performance data available
    // Return null if no performance data available
    return null;
  };

  /**
   * Format percentage with color
   */
  const formatPerformance = (percentage) => {
    if (percentage === null || percentage === undefined) {
      return (
        <span className="text-gray-500 text-sm">
          Not tracked
        </span>
      );
    }
    
    const isPositive = percentage >= 0;
    const color = isPositive ? 'text-green-600' : 'text-red-600';
    const arrow = isPositive ? '↑' : '↓';
    
    return (
      <span className={`font-semibold ${color}`}>
        {arrow} {Math.abs(percentage).toFixed(2)}%
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {!isMobileEmbed && <Navbar />}

      <main
        className={`flex-grow max-w-7xl mx-auto px-4 w-full ${isMobileEmbed ? 'py-3' : 'py-8'}`}
      >
        {/* Header (hidden in mobile app WebView — app shows its own chrome) */}
        {!isMobileEmbed && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Results History</h1>
            <p className="text-gray-600">
              Track and analyze your historical scan results and their performance over time
            </p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <p className="text-green-800">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage('')}
                className="text-green-600 hover:text-green-800"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Filter Panel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Advanced Filters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Smart Search
              </label>
              <input
                type="text"
                value={filters.search_query}
                onChange={(e) => handleFilterChange('search_query', e.target.value)}
                placeholder="Search stocks..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Model Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model Type
              </label>
              <select
                value={filters.model_type}
                onChange={(e) => handleFilterChange('model_type', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Models</option>
                <option value="short-term">Short-Term</option>
                <option value="long-term">Long-Term</option>
                <option value="swing-model">Swing Model</option>
                <option value="fno-breakout">F&O Breakout</option>
                <option value="fno-breakdown">F&O Breakdown</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={applyFilters}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:bg-gray-400"
            >
              Apply Filters
            </button>
            
            <button
              onClick={handleResetFilters}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Grouped Results (Accordion) */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading results...</p>
            </div>
          ) : Object.keys(filteredResults).length === 0 ? (
            <div className="p-12 text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600">
                {Object.values(filters).some(f => f) 
                  ? 'Try adjusting your filters' 
                  : 'Run your first scan to start tracking results'}
              </p>
            </div>
          ) : (
            <>
              {/* Accordion rendering */}
              <div>
                {Object.keys(filteredResults).map((dateKey) => (
                  <div key={dateKey} className="border-b border-gray-200">
                    {/* Date Row */}
                    <div className="flex items-center justify-between px-6 py-4 bg-gray-50">
                      <button onClick={() => toggleDate(dateKey)} className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {dateKey} {openDates[dateKey] ? '▲' : '▼'}
                        </h3>
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleTrackPerformanceByDate(dateKey)}
                          disabled={trackingInProgress}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm transition-colors disabled:bg-gray-400"
                        >
                          {trackingInProgress ? 'Updating...' : '🔄 Update Performance'}
                        </button>
                        <button
                          onClick={() => openDeleteModal('date', { date: dateKey })}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm transition-colors"
                        >
                          🗑️ Delete All
                        </button>
                      </div>
                    </div>

                    {/* Models under date */}
                    {openDates[dateKey] && (
                      <div className="px-4 py-2">
                        {Object.keys(filteredResults[dateKey]).map((modelKey) => {
                          const mk = `${dateKey}__${modelKey}`;
                          const items = filteredResults[dateKey][modelKey] || [];
                          
                          // Determine badge color based on model type
                          const getBadgeColor = (type) => {
                            if (type === 'fno-breakout') return 'bg-green-100 border-green-300 text-green-800';
                            if (type === 'fno-breakdown') return 'bg-red-100 border-red-300 text-red-800';
                            if (type === 'short-term') return 'bg-blue-100 border-blue-300 text-blue-800';
                            if (type === 'long-term') return 'bg-purple-100 border-purple-300 text-purple-800';
                            if (type === 'swing-model') return 'bg-orange-100 border-orange-300 text-orange-800';
                            return 'bg-gray-100 border-gray-300 text-gray-800';
                          };
                          
                          return (
                            <div key={mk} className={`mb-4 border-2 rounded-lg ${getBadgeColor(modelKey)}`}>
                              <div className="flex items-center justify-between px-4 py-3">
                                <button onClick={() => toggleModel(dateKey, modelKey)} className="text-left flex-1">
                                  <h4 className="font-bold text-lg">
                                    {formatModelType(modelKey)} ({items.length}) {openModels[mk] ? '▲' : '▼'}
                                  </h4>
                                </button>
                              </div>

                              {openModels[mk] && (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                          {modelKey.startsWith('fno-') ? 'Stock / Option' : 'Stock'}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                          {modelKey.startsWith('fno-') ? 'Option Premium' : 'Entry'}
                                        </th>
                                        {modelKey.startsWith('fno-') && (
                                          <>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Target</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Stop Loss</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Rank</th>
                                          </>
                                        )}
                                        {!modelKey.startsWith('fno-') && (
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Performance</th>
                                        )}
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {items.map((r) => {
                                        const isFno = modelKey.startsWith('fno-');
                                        const resultData = r.result_data || {};
                                        
                                        return (
                                          <tr key={r.id}>
                                            <td className="px-4 py-2 text-sm">
                                              <button
                                                onClick={() => {
                                                  setSelectedSymbolForChart(r.stock_symbol);
                                                  setIsChartModalOpen(true);
                                                }}
                                                className="text-blue-600 hover:text-blue-800 font-semibold hover:underline cursor-pointer"
                                                title="Click to view chart"
                                              >
                                                {r.stock_symbol}
                                              </button>
                                              {isFno && resultData.option_symbol && (
                                                <div className="text-xs text-gray-600 font-mono mt-1">
                                                  {resultData.option_symbol}
                                                </div>
                                              )}
                                              {isFno && resultData.stock_pct_change && (
                                                <div className={`text-xs font-semibold mt-1 ${
                                                  resultData.stock_pct_change > 0 ? 'text-green-600' : 'text-red-600'
                                                }`}>
                                                  {resultData.stock_pct_change > 0 ? '+' : ''}{resultData.stock_pct_change}%
                                                </div>
                                              )}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">
                                              ₹{r.entry_price ? Number(r.entry_price).toFixed(2) : 'N/A'}
                                            </td>
                                            
                                            {isFno && (
                                              <>
                                                <td className="px-4 py-2 text-sm text-green-600 font-semibold">
                                                  ₹{resultData.target_price ? Number(resultData.target_price).toFixed(2) : 'N/A'}
                                                  {resultData.target_percent && (
                                                    <span className="text-xs ml-1">({resultData.target_percent}%)</span>
                                                  )}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-red-600 font-semibold">
                                                  ₹{resultData.stop_loss_price ? Number(resultData.stop_loss_price).toFixed(2) : 'N/A'}
                                                  {resultData.stop_loss_percent && (
                                                    <span className="text-xs ml-1">({resultData.stop_loss_percent}%)</span>
                                                  )}
                                                </td>
                                                <td className="px-4 py-2 text-sm">
                                                  {resultData.rank ? (
                                                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                                                      resultData.rank === 1 
                                                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' 
                                                        : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                      #{resultData.rank}
                                                    </span>
                                                  ) : 'N/A'}
                                                </td>
                                              </>
                                            )}
                                            
                                            {!isFno && (
                                              <td className="px-4 py-2 text-sm">{formatPerformance(calculatePerformance(r))}</td>
                                            )}
                                            
                                            <td className="px-4 py-2 text-sm">
                                              <div className="flex gap-2">
                                                {!isFno && (
                                                  <button
                                                    onClick={() => handleTrackSingle(r.id)}
                                                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                                                  >
                                                    Update
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() => openDeleteModal('result', { id: r.id, symbol: r.stock_symbol })}
                                                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {!isMobileEmbed && <Footer />}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Confirm Deletion
            </h3>
            
            <p className="text-gray-700 mb-6">
              {deleteModal.type === 'date' ? (
                <>
                  Are you sure you want to delete <strong>all scan results</strong> for <strong>{deleteModal.targetDate}</strong>?
                  <br /><br />
                  This action cannot be undone and will remove all associated performance tracking data.
                </>
              ) : (
                <>
                  Are you sure you want to delete the scan result for <strong>{deleteModal.targetSymbol}</strong>?
                  <br /><br />
                  This action cannot be undone and will remove all associated performance tracking data.
                </>
              )}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Chart Modal */}
      <StockChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        symbol={selectedSymbolForChart}
      />
    </div>
  );
};

export default ResultsHistoryPage;

